import { useMemo, useState } from 'react'
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { FileTextIcon, ImagesIcon, MoreHorizontalIcon, PlusIcon, Trash2Icon } from 'lucide-react'

import { slugify } from '@ems/cms/slug'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@ems/ui/components/alert-dialog'
import { Button, buttonVariants } from '@ems/ui/components/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@ems/ui/components/dropdown-menu'
import { Input } from '@ems/ui/components/input'
import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { Field } from '@ems/ui/patterns/field'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'

import {
  collectionAnchor,
  createDocument,
  documentTone,
  newDocumentData,
  statusLine,
  titleFieldOf,
  type Collection,
  type DocumentSummary,
} from '#admin/features/cms/api.ts'
import {
  collectionsQuery,
  documentQuery,
  documentsQuery,
  editionLocalesQuery,
} from '#admin/features/cms/queries.ts'
import { CollectionCard } from '#admin/features/cms/collection-card.tsx'
import {
  isFiltering,
  languageTabs,
  matchesFilter,
  NO_FILTER,
  pageOf,
  rowsInLanguage,
  type ContentFilter,
  type StatusFilter,
} from '#admin/features/cms/content-filters.ts'
import { ContentToolbar } from '#admin/features/cms/content-toolbar.tsx'
import { TranslationCell } from '#admin/features/cms/translation-cell.tsx'
import {
  DeleteDocumentDialog,
  focusCollection,
} from '#admin/features/cms/delete-document-dialog.tsx'
import { deleteOffer } from '#admin/features/cms/deletion.ts'
import {
  groupTranslations,
  languageName,
  translationKey,
} from '#admin/features/cms/translations.ts'
import { toastError } from '#admin/lib/toast-error.ts'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { ContentIndexSkeleton } from '#admin/features/cms/content-skeletons.tsx'

/**
 * Everything on the public site, in one list.
 *
 * Grouped by collection rather than filtered to one, because the question an editor arrives with
 * is "what have I got" far more often than "show me the news" — and a page that opens on one
 * collection makes the other eleven invisible until somebody finds the filter.
 *
 * **The collections come from the API, not from this file.** A thirteenth collection appears
 * here the day it is deployed, with its label, its singular name and its field list, and nothing
 * in this console changes.
 *
 * **Each collection is a card with a tab per language and one page of rows at a time**
 * (`CollectionCard`). Sixteen full tables made a page that was a long scroll. A page replaces the
 * rows, so a card keeps its height and does not push the cards under it down.
 */
export const Route = createFileRoute('/_authenticated/cms/content/')({
  pendingComponent: ContentIndexSkeleton,
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(collectionsQuery()),
      context.queryClient.ensureQueryData(documentsQuery()),
      context.queryClient.ensureQueryData(editionLocalesQuery()),
    ]),
  component: ContentPage,
})

function ContentPage() {
  const { data: session } = useSuspenseQuery(sessionQuery())
  const { data: collections } = useSuspenseQuery(collectionsQuery())
  const { data: documents } = useSuspenseQuery(documentsQuery())
  const { data: locales } = useSuspenseQuery(editionLocalesQuery())
  const translations = groupTranslations(documents.items)

  const byCollection = new Map<string, DocumentSummary[]>()
  for (const document of documents.items) {
    byCollection.set(document.collection, [
      ...(byCollection.get(document.collection) ?? []),
      document,
    ])
  }

  const scheduled = documents.items.filter((d) => d.status === 'scheduled')

  const [filter, setFilter] = useState<ContentFilter>(NO_FILTER)
  const statusCounts: Record<StatusFilter, number> = {
    all: documents.items.length,
    draft: documents.items.filter((document) => document.status === 'draft').length,
    published: documents.items.filter((document) => document.status === 'published').length,
    scheduled: scheduled.length,
  }

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Content"
        description={
          <>
            {`${documents.items.length} document${
              documents.items.length === 1 ? '' : 's'
            } across ${collections.items.length} collections`}
            {scheduled.length > 0 ? (
              <>
                {' · '}
                {/* Shows the scheduled documents only: the ones this line counts. */}
                <button
                  type="button"
                  onClick={() => setFilter({ query: '', status: 'scheduled' })}
                  className="focus-visible:ring-ring/50 hover:text-foreground rounded-sm underline decoration-dotted underline-offset-2 outline-none focus-visible:ring-[3px]"
                >
                  {scheduled.length} waiting to go live
                </button>
              </>
            ) : null}
          </>
        }
        actions={
          <Link to="/cms/media" className={buttonVariants({ variant: 'outline', size: 'sm' })}>
            <ImagesIcon />
            Media library
          </Link>
        }
      />

      {can(session, 'cms.manage') ? null : <ReadOnlyNotice area="the site's content" />}

      <div className="-mt-4">
        <ContentToolbar filter={filter} onChange={setFilter} statusCounts={statusCounts} />
      </div>

      {collections.items.map((collection) => (
        <CollectionSection
          key={collection.key}
          collection={collection}
          rows={byCollection.get(collection.key) ?? []}
          filter={filter}
          edition={locales}
          translations={translations}
        />
      ))}
    </div>
  )
}

function CollectionSection({
  collection,
  rows,
  filter,
  edition,
  translations,
}: {
  collection: Collection
  rows: DocumentSummary[]
  /** The page's search and status pill. The card lists the rows that match. */
  filter: ContentFilter
  edition: { defaultLocale: string; locales: readonly string[] }
  translations: ReadonlyMap<string, DocumentSummary[]>
}) {
  const queryClient = useQueryClient()
  const locales = edition.locales
  const filtering = isFiltering(filter)
  const matching = filtering ? rows.filter((row) => matchesFilter(row, filter)) : rows
  const languages = languageTabs(collection, rows, edition)
  const [chosen, setChosen] = useState<string | undefined>(undefined)
  // The edition's default language until the editor picks one.
  const language = languages.length === 0 ? undefined : (chosen ?? languages[0])
  const inLanguage = rowsInLanguage(matching, language)
  // The page belongs to one filter. A new search or pill starts again at the first page.
  const filterKey = `${filter.status}|${filter.query.trim().toLowerCase()}`
  const [paged, setPaged] = useState({ filterKey, page: 0 })
  const page = paged.filterKey === filterKey ? paged.page : 0
  const setPage = (next: number) => setPaged({ filterKey, page: next })
  const shown = pageOf(inLanguage, page)
  const { data: session } = useSuspenseQuery(sessionQuery())
  const canManage = can(session, 'cms.manage')
  const [deleting, setDeleting] = useState<DocumentSummary | null>(null)
  const deletingOffer =
    deleting === null ? null : deleteOffer({ canManage, collection, status: deleting.status })

  const columns = useMemo<ColumnDef<DocumentSummary, never>[]>(
    () => [
      {
        accessorKey: 'title',
        header: 'Title',
        size: 3,
        meta: { primary: true, truncate: true },
        cell: ({ row }) => (
          <Link
            to="/cms/content/$id"
            params={{ id: row.original.id }}
            // The editor reads the document first. Asked for on the way to the click, so the
            // editor opens with the document already here.
            onMouseEnter={() => void queryClient.prefetchQuery(documentQuery(row.original.id))}
            onFocus={() => void queryClient.prefetchQuery(documentQuery(row.original.id))}
            className="focus-visible:ring-ring rounded-sm font-medium hover:underline focus-visible:ring-2 focus-visible:outline-none"
          >
            {row.original.title === '' ? 'Untitled' : row.original.title}
          </Link>
        ),
      },
      {
        id: 'path',
        header: 'Address',
        size: 2,
        meta: { truncate: true },
        cell: ({ row }) => (
          <span
            className="text-muted-foreground font-mono text-xs"
            title={row.original.path ?? undefined}
          >
            {row.original.path ?? '—'}
          </span>
        ),
      },
      // No Language column: the card's language tab says it for every row.
      ...(collection.localized
        ? [
            {
              id: 'translations',
              header: 'Translations',
              cell: ({ row }) => (
                <TranslationCell
                  locales={locales}
                  versions={translations.get(translationKey(row.original)) ?? [row.original]}
                />
              ),
            } satisfies ColumnDef<DocumentSummary, never>,
          ]
        : []),
      {
        accessorKey: 'status',
        header: 'State',
        /**
         * The state, as a badge plus a date line.
         *
         * Color alone would not carry it: roughly one reader in twelve cannot separate the green
         * from the amber, and this is the column that decides whether somebody publishes something
         * twice. `StatusBadge` guarantees the word and the shape; `statusLine` adds the *when*,
         * which is the part that answers "has the scheduled one gone out yet".
         *
         * Rendered, not a `title` tooltip. A tooltip is unreachable by keyboard and invisible on
         * touch.
         */
        cell: ({ row }) => (
          /*
            One line, not two stacked. The badge and the date each carried their own row before,
            which made every row roughly seventy pixels tall — and sixteen tables of those is what
            turned the page into a grid of lines. It never wraps: the title and address columns
            are the ones that give way when the table is narrow.
          */
          <span className="inline-flex items-center gap-2">
            <StatusBadge tone={documentTone(row.original.status)}>
              {row.original.status}
            </StatusBadge>
            <span className="text-muted-foreground text-xs">{statusLine(row.original)}</span>
          </span>
        ),
      },
      ...(deleteOffer({ canManage, collection, status: 'draft' }).offered
        ? [
            {
              id: 'actions',
              header: () => <span className="sr-only">Actions</span>,
              meta: { align: 'end', hideOnCard: true },
              cell: ({ row }) => <DocumentActions document={row.original} onDelete={setDeleting} />,
            } satisfies ColumnDef<DocumentSummary, never>,
          ]
        : []),
    ],
    [collection, canManage, locales, translations, setDeleting, queryClient],
  )

  const noun =
    rows.length === 1 ? collection.singular.toLowerCase() : collection.label.toLowerCase()
  const description =
    collection.routeKind === 'embedded'
      ? // Said plainly, because "why isn't the sponsors page showing up?" is otherwise a support
        // ticket about a page that was never meant to exist.
        'Composed into other pages — no page of its own.'
      : `${rows.length} ${noun}${collection.localized ? '' : ', the same in every language'}.`

  return (
    <>
      <CollectionCard
        id={collectionAnchor(collection.key)}
        title={collection.label}
        total={rows.length}
        description={description}
        languages={languages}
        language={language}
        onLanguageChange={(next) => {
          setChosen(next)
          // Another language is another list: it starts again at its first page.
          setPage(0)
        }}
        countOf={(locale) => matching.filter((row) => row.locale === locale).length}
        actions={
          <NewDocument
            collection={collection}
            // A singleton is one document at a fixed address. Offering "New" once it exists would
            // produce a request the unique index refuses — a 409 for pressing a button the console
            // showed, which teaches editors to distrust refusals.
            disabled={collection.routeKind === 'singleton' && rows.length > 0}
          />
        }
        pagination={{
          from: shown.from,
          to: shown.to,
          of: inLanguage.length,
          page: shown.page,
          pages: shown.pages,
          onPage: setPage,
        }}
      >
        <DataTable
          caption={
            language === undefined
              ? collection.label
              : `${collection.label}, ${languageName(language)}`
          }
          columns={columns}
          data={shown.rows}
          getRowId={(document) => document.id}
          empty={
            filtering && rows.length > 0 ? (
              <FilteredEmpty filter={filter} />
            ) : (
              <EmptyState
                icon={<FileTextIcon />}
                title={
                  language === undefined || rows.length === 0
                    ? `No ${collection.label.toLowerCase()} yet`
                    : `No ${collection.label.toLowerCase()} in ${languageName(language)} yet`
                }
                description={
                  language === undefined || rows.length === 0
                    ? `Create the first ${collection.singular.toLowerCase()} and it appears here as a draft.`
                    : `Open a ${collection.singular.toLowerCase()} in another language and add this language to it.`
                }
                {...(rows.length === 0
                  ? { action: <NewDocument collection={collection} disabled={false} /> }
                  : {})}
              />
            )
          }
        />
      </CollectionCard>
      {deleting !== null && deletingOffer?.offered === true ? (
        <DeleteDocumentDialog
          document={deleting}
          collection={collection}
          takeDownFirst={deletingOffer.takeDownFirst}
          onOpenChange={(dialogOpen) => {
            if (!dialogOpen) setDeleting(null)
          }}
          onDeleted={() => {
            setDeleting(null)
            focusCollection(collection.key)
          }}
        />
      ) : null}
    </>
  )
}

function DocumentActions({
  document,
  onDelete,
}: {
  document: DocumentSummary
  onDelete: (document: DocumentSummary) => void
}) {
  const name = document.title === '' ? 'Untitled' : document.title
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={`Actions for ${name} (${document.locale})`}>
          <MoreHorizontalIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <Link to="/cms/content/$id" params={{ id: document.id }}>
            Open
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem variant="destructive" onSelect={() => onDelete(document)}>
          <Trash2Icon />
          Delete…
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * Create a draft and go straight to it.
 *
 * The body is `blankDocument(collection)` — every field present, with a value of the right kind
 * rather than `undefined`, so the editor's inputs are controlled from the first render. Beyond
 * that, validation happens at **publish**, which is what lets an editor start a page on Tuesday
 * and finish it on Thursday.
 *
 * **Except the title, for anything that is not a singleton.** Its slug is derived from the title
 * when the draft is created, and the API refuses a title that yields none — so pressing "New" on
 * an empty form was a toast saying "A slug is needed" for a button that asked for nothing. The
 * title is asked for first, in a dialog. A singleton has a fixed slug and is still one click.
 */
function NewDocument({ collection, disabled }: { collection: Collection; disabled: boolean }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [asking, setAsking] = useState(false)
  // Creating is a write: a view-only role sees the documents and no "New" button.
  const { data: session } = useSuspenseQuery(sessionQuery())

  const create = useMutation({
    mutationFn: (title: string | undefined) =>
      createDocument({ collection: collection.key, data: newDocumentData(collection, title) }),
    onSuccess: async (document) => {
      await queryClient.invalidateQueries({ queryKey: ['cms', 'documents'] })
      await navigate({ to: '/cms/content/$id', params: { id: document.id } })
    },
    // A toast rather than a panel above the list. The failure belongs to the button that was
    // pressed, not to the page, and a page-level error box pushes every collection down the
    // screen to report something about one of them. The dialog stays open, so the title typed
    // is still there to correct.
    onError: toastError,
  })

  if (disabled || !can(session, 'cms.manage')) return null

  const singleton = collection.routeKind === 'singleton'

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => (singleton ? create.mutate(undefined) : setAsking(true))}
        disabled={create.isPending}
      >
        <PlusIcon />
        {create.isPending && singleton ? 'Creating…' : `New ${collection.singular.toLowerCase()}`}
      </Button>
      {singleton ? null : (
        <TitleDialog
          collection={collection}
          open={asking}
          onOpenChange={setAsking}
          busy={create.isPending}
          onConfirm={(title) => create.mutate(title)}
        />
      )}
    </>
  )
}

/**
 * Ask for the one thing a new draft cannot do without.
 *
 * Create stays disabled until the title would produce an address — not only while it is empty.
 * `"!!!"` is not empty, and it slugifies to nothing just the same; this is the `slugify` the API
 * runs, so the check cannot disagree with the refusal it is there to prevent. The slug itself is
 * still the API's to choose, because only the API knows which ones are taken.
 */
function TitleDialog({
  collection,
  open,
  onOpenChange,
  busy,
  onConfirm,
}: {
  collection: Collection
  open: boolean
  onOpenChange: (open: boolean) => void
  busy: boolean
  onConfirm: (title: string) => void
}) {
  const [title, setTitle] = useState('')
  const field = titleFieldOf(collection)
  const maxLength = field?.maxLength ?? undefined
  const empty = title.trim() === ''
  const noAddress = !empty && slugify(title) === ''

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setTitle('')
        onOpenChange(next)
      }}
    >
      <AlertDialogContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            if (!empty && !noAddress && !busy) onConfirm(title.trim())
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>New {collection.singular.toLowerCase()}</AlertDialogTitle>
            <AlertDialogDescription>
              Its address is made from this, so it is asked for now. Everything else can be filled
              in on the draft.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <Field
            label={field?.label ?? 'Title'}
            required
            {...(noAddress ? { error: 'Use at least one letter or number.' } : {})}
          >
            {(props) => (
              <Input
                {...props}
                value={title}
                {...(maxLength === undefined ? {} : { maxLength })}
                onChange={(event) => setTitle(event.target.value)}
              />
            )}
          </Field>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <Button type="submit" disabled={empty || noAddress || busy}>
              {busy ? 'Creating…' : 'Create'}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  )
}

/** The words for an empty card under a status pill: "No drafts in this collection". */
const STATUS_EMPTY_NOUN: Record<StatusFilter, string> = {
  all: 'documents',
  draft: 'drafts',
  published: 'published documents',
  scheduled: 'scheduled documents',
}

/** A card whose rows the search or the status pill leaves out. The rows exist, so no "New". */
function FilteredEmpty({ filter }: { filter: ContentFilter }) {
  const query = filter.query.trim()
  return (
    <p className="text-muted-foreground px-4 py-6 text-center text-sm">
      {query === ''
        ? `No ${STATUS_EMPTY_NOUN[filter.status]} in this collection`
        : `No documents matching “${query}” in this collection`}
    </p>
  )
}
